import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, provideBrowserGlobalErrorListeners, provideZonelessChangeDetection } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { PreloadAllModules, provideRouter, withComponentInputBinding, withPreloading } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { routes } from './app.routes';
import { AuthInterceptor } from './core/http/auth.interceptor';
import { CorrelationInterceptor } from './core/http/correlation.interceptor';
import { GlobalErrorHandler } from './core/error';
import { AuthService } from './core/auth/auth.service';
import { PersonnelApi } from './features/resources/personnel/data/personnel.api';
import { provideDefaultClient } from './core/api/generated';
import { LanguageService } from './core/i18n/language.service';
import { LoadingService } from './core/services/loading/loading.service';
import { environment } from '@env/environment';

/**
 * Runs before the router activates any route.
 * If there is a valid token in sessionStorage (e.g. after page refresh),
 * reload the personnel profile so that AuthService.userValue is populated
 * before any component constructor or guard runs.
 * If the token is expired or the request fails, clear auth state so the
 * guard redirects to Keycloak login.
 */
function initPersonnel(
  authService: AuthService,
  personnelApi: PersonnelApi,
  langService: LanguageService,
  loadingService: InstanceType<typeof LoadingService>
): () => Promise<void> {
  return async () => {
    loadingService.show();

    try {
      if (!authService.isLoggedIn()) {
        return;
      }
      if (authService.getUser()) {
        return;
      }
      // Skip heavy init if user is landing on the consent page — the gateway will block everything else anyway
      if (typeof window !== 'undefined' && window.location.pathname.startsWith('/consent')) {
        return;
      }

      try {
        // 3s timeout: if the gateway doesn't respond, the app still boots (without
        // personnel data) and the guard will redirect to login when needed.
        const timeout = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('init_timeout')), 3000)
        );
        const personnel = await Promise.race([firstValueFrom(personnelApi.getSelf()), timeout]);
        const personnelId = personnel?.id ?? personnel?.per_id;
        if (personnelId) {
          authService.setUserValue({ personnel, token: authService.getAccessToken()! });
          langService.setPredefinedLanguageById(personnel.lang_id ?? 0);
        } else {
          authService.clear();
        }
      } catch (err: any) {
        // 401/403 = token invalid or user not provisioned → force re-auth
        // Network error = backend temporarily down → keep token so user can retry
        const status = err?.status ?? err?.error?.status;
        const msg = String(err?.error?.message ?? err?.error?.detail ?? '').toLowerCase();
        if (status === 401) {
          authService.clear();
        }
        if (status === 403 && msg.includes('consent')) {
          return;
        }
      }
    } finally {
      loadingService.hide();
    }
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideAnimations(),
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes, withPreloading(PreloadAllModules), withComponentInputBinding()),
    provideHttpClient(withInterceptorsFromDi()),
    provideDefaultClient({ basePath: environment.url_resources_api }),
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CorrelationInterceptor, multi: true },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    {
      provide: APP_INITIALIZER,
      useFactory: initPersonnel,
      deps: [AuthService, PersonnelApi, LanguageService, LoadingService],
      multi: true,
    },
  ]
};
